import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowDownToLine, ChevronDown, ClipboardList, House, LogOut, MoreHorizontal, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { ReclamacionesSesion } from '@discopico/domain';
import { obtenerSupabase } from '../../nucleo/supabase/cliente.js';
import { IndicadorConexion } from '../estado-conexion/IndicadorConexion.js';
import { destinosDeBarra, ETIQUETA_ROL, gruposVisibles, type Destino } from './mapa.js';
import { rutaInicialDe } from '../../nucleo/autorizacion/sesion.js';

/**
 * El marco de toda pantalla con sesión, como lo dibuja `docs/diseno-frontend`: barra inferior de
 * cuatro destinos en el teléfono (pantalla 1a) y menú lateral fijo en la oficina (pantalla 1c).
 *
 * LA PRIMERA VERSIÓN, DEL MISMO DÍA, SE HIZO SIN MIRAR EL DISEÑO, y era la alternativa que el
 * diseño descartó (1b: un botón «Menú» que despliega todo). El diseño estaba en el repositorio
 * desde el 2026-09-16 y ninguna tarea lo citaba. Lo que dice la barra inferior, y por eso gana, es
 * que lo que el operario hace de madrugada está siempre a un toque sin abrir nada; el precio es que
 * sólo caben cuatro destinos, y el resto vive en «Más».
 *
 * EL TEMA NO SE DECIDE ACÁ. Oscuro en el teléfono y claro en la oficina lo resuelve el ancho en
 * `global.css`, redefiniendo los mismos tokens, así que este componente usa `bg-hundido` y en cada
 * aparato sale el color que corresponde.
 *
 * EL CONTENIDO VA EN UN `<div>` Y NO EN UN `<main>`: veintiuna pantallas ya dibujan su propio
 * `<main>`, y dos anidados son un documento inválido.
 */
/** `aviso` va arriba del pie del menú; lo pone la ruta raíz, para que este archivo no dependa de ninguna funcionalidad. */
export function Marco({ sesion, aviso, children }: { sesion: ReclamacionesSesion; aviso?: ReactNode; children: ReactNode }) {
  const [masAbierto, setMasAbierto] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const grupos = gruposVisibles(sesion);
  const destinos = destinosDeBarra(sesion);

  async function salir() {
    await obtenerSupabase().auth.signOut();
    // Sin `motivo`: salir no es un rebote del guardián y no hay nada que explicar en pantalla.
    void navigate({ to: '/ingresar', search: { motivo: undefined }, replace: true });
  }

  // EL MENÚ SE AGRUPA Y NO SE DESPLAZA, pedido del dueño del negocio el 2026-09-28 mirando la
  // oficina: con los treinta enlaces a la vista y la ruta debajo de cada uno, la columna medía el
  // doble de la pantalla. Ahora se ven los títulos de los grupos y sólo se despliega el de la
  // pantalla en la que se está. La ruta en letra chica se fue: le decía algo al que construye, no al que usa.
  //
  // UNO SOLO ABIERTO A LA VEZ, pedido del 2026-10-06: abrir un grupo cierra el otro, así la columna nunca
  // pasa del alto de la pantalla. Lo elegido a mano vale mientras no se cambie de pantalla; al cambiar,
  // vuelve a abrirse el grupo de la pantalla nueva.
  const grupoActual = grupos.find((g) => g.enlaces.some((e) => e.a === pathname))?.titulo;
  const [elegido, setElegido] = useState<{ en: string; titulo: string | null } | null>(null);
  const abierto = (titulo: string) => (elegido?.en === pathname ? elegido.titulo : grupoActual) === titulo;

  const menuCompleto = (alElegir: () => void) => (
    <ul className="grid gap-1">
      {grupos.map((g) => {
        const id = `grupo-${g.titulo}`;
        return (
          <li key={g.titulo}>
            <button
              type="button"
              aria-expanded={abierto(g.titulo)}
              aria-controls={id}
              onClick={() => setElegido({ en: pathname, titulo: abierto(g.titulo) ? null : g.titulo })}
              // EL GRUPO ABIERTO SE MARCA CON UNA BARRA Y NO CON TEXTO CORAL. Coral sobre la crema de
              // la barra lateral da 2,34 a 1, y el mínimo es 4,5: medido en las 66 capturas de
              // escritorio de la revisión de UX del 2026-09-28. La tinta da 15,6 a 1 en la oficina y
              // sigue siendo crema en el teléfono, así que el acento de marca se mueve al borde, que
              // es decorativo y no carga con la legibilidad del texto.
              className={`flex min-h-11 w-full items-center justify-between rounded-[10px] px-3 text-left text-[15px] font-semibold md:min-h-9 ${
                g.titulo === grupoActual ? 'border-l-[3px] border-coral pl-[9px]' : ''
              }`}
            >
              {g.titulo}
              <ChevronDown
                strokeWidth={1.25}
                aria-hidden
                className={`size-4 transition-transform ${abierto(g.titulo) ? 'rotate-180' : ''}`}
              />
            </button>
            {abierto(g.titulo) && (
              <ul id={id} className="grid pb-1.5 pl-2">
                {g.enlaces.map((e) => (
                  <li key={e.a} className={e.dentro ? 'ml-3 border-l-2 border-coral/50 pl-1' : ''}>
                    <Link
                      to={e.a}
                      onClick={alElegir}
                      activeOptions={{ exact: true }}
                      activeProps={{ 'aria-current': 'page', className: 'bg-coral/20 font-semibold' }}
                      className={`flex min-h-11 items-center rounded-[10px] px-3 text-[14.5px] md:min-h-8 md:text-[14px] ${
                        e.encabeza ? 'font-semibold' : ''
                      }`}
                    >
                      {e.texto}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  const pie = (
    <div className="grid gap-1 border-t border-tinta/10 px-3 pt-2.5">
      {aviso}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13.5px] font-semibold">
          {sesion.superAdministrador
            ? 'Super administrador'
            : sesion.puedeElegirRol
              ? `Con el rol de ${ETIQUETA_ROL[sesion.rol].toLowerCase()}`
              : ETIQUETA_ROL[sesion.rol]}
        </p>
        <button
          type="button"
          onClick={() => void salir()}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-[10px] px-2 text-[14px] md:min-h-8"
        >
          <LogOut strokeWidth={1.25} aria-hidden className="size-4" />
          Salir
        </button>
      </div>
      {sesion.puedeElegirRol && (
        <Link
          to="/elegir-rol"
          onClick={() => setMasAbierto(false)}
          className="inline-flex min-h-11 items-center text-[14px] underline md:min-h-8"
        >
          Cambiar de rol
        </Link>
      )}
      {/* Los dos indicadores del marco llevan su propio identificador de prueba. Siete pantallas
          traen el suyo, que es el que miden las pruebas, y el de la cabecera está en el documento
          aunque el ancho lo esconda: con el mismo identificador, `cierre-cuadrado.spec.ts`
          encontró dos y cortó el día que nació este marco. */}
      <IndicadorConexion idPrueba="indicador-conexion-menu" enMarco />
    </div>
  );

  return (
    <div className="min-h-screen md:flex">
      {/* Teléfono: cabecera con la marca y la conexión, que es lo único que no puede faltar. */}
      {/* Al imprimir (el certificado de retenciones) sólo sale el contenido: el marco se esconde. */}
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 bg-hundido px-4 py-2.5 md:hidden print:hidden">
        <Marca />
        <IndicadorConexion idPrueba="indicador-conexion-marco" enMarco />
      </header>

      {/* Oficina: el menú completo, fijo. */}
      <nav
        aria-label="Menú principal"
        className="hidden bg-hundido md:sticky md:top-0 md:flex md:h-screen md:w-[252px] md:shrink-0 md:flex-col print:hidden"
      >
        <div className="border-b border-tinta/10 px-5 py-3">
          {/* El logo lleva al inicio de cada rol: es la puerta de las pantallas de aterrizaje, que no van en el menú. */}
          <Link to={rutaInicialDe(sesion.rol)} aria-label="Inicio">
            <Marca />
          </Link>
        </div>
        {/* EL MENÚ SE DESPLAZA Y EL PIE NO. Con un grupo largo abierto la columna entera se desplazaba y
            el pie, con «Cambiar de rol» y la conexión, quedaba debajo del borde de la pantalla, medido
            en las capturas del 2026-09-30. `min-h-0` deja que el hijo de la columna flexible se encoja. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-2">{menuCompleto(() => {})}</div>
        <div className="px-2 pb-3">{pie}</div>
      </nav>

      <div className="min-w-0 flex-1 pb-[76px] md:pb-0 print:pb-0">{children}</div>

      {/* Teléfono: la barra inferior. Sesenta píxeles de alto, el destino activo en coral. */}
      <nav
        aria-label="Destinos principales"
        className="fixed inset-x-0 bottom-0 z-30 grid h-[60px] auto-cols-fr grid-flow-col border-t border-tinta/10 bg-hundido md:hidden print:hidden"
      >
        {destinos.map((d) => (
          <DestinoDeBarra
            key={d.texto}
            destino={d}
            activo={estaEn(pathname, d.a) || (d.texto === 'Jornada' && pathname.startsWith('/jornada'))}
          />
        ))}
        <button
          type="button"
          onClick={() => setMasAbierto(true)}
          aria-expanded={masAbierto}
          aria-controls="menu-mas"
          className="flex flex-col items-center justify-center gap-0.5 text-[12.5px]"
        >
          <MoreHorizontal strokeWidth={1.25} aria-hidden />
          Más
        </button>
      </nav>

      {masAbierto && (
        <div
          id="menu-mas"
          role="dialog"
          aria-modal="true"
          aria-label="Todas las pantallas"
          className="fixed inset-0 z-40 overflow-y-auto bg-base md:hidden"
        >
          <div className="sticky top-0 flex items-center justify-between bg-base px-4 py-2.5">
            <span className="font-titulo text-[19px] font-semibold">Todas las pantallas</span>
            <button
              type="button"
              onClick={() => setMasAbierto(false)}
              className="inline-flex min-h-12 min-w-12 items-center justify-center gap-1"
            >
              <X strokeWidth={1.25} aria-hidden />
              Cerrar
            </button>
          </div>
          <div className="px-2 pb-8 pt-2">
            {menuCompleto(() => setMasAbierto(false))}
            {pie}
          </div>
        </div>
      )}
    </div>
  );
}

const ICONO: Record<Destino['texto'], typeof House> = {
  Inicio: House,
  Jornada: ClipboardList,
  Egresos: ArrowDownToLine,
};

function DestinoDeBarra({ destino, activo }: { destino: Destino; activo: boolean }) {
  const Icono = ICONO[destino.texto];
  return (
    <Link
      to={destino.a}
      aria-current={activo ? 'page' : undefined}
      // EL ACTIVO LLEVA EL CORAL EN EL ÍCONO Y EL PESO EN LA PALABRA. Con el tema claro en todos los
      // aparatos (adjudicación 256), coral sobre crema da 2,34 a 1 y la palabra no se leía; es lo mismo
      // que ya se resolvió en el menú lateral. El ícono es decorativo y la palabra queda en tinta.
      className={`flex flex-col items-center justify-center gap-0.5 text-[12.5px] ${activo ? 'font-semibold' : ''}`}
    >
      <Icono strokeWidth={activo ? 2 : 1.25} aria-hidden className={activo ? 'text-coral' : ''} />
      {destino.texto}
    </Link>
  );
}

/** Activo por prefijo: `/jornada/conteo` mantiene encendida la pestaña de la jornada. */
function estaEn(pathname: string, a: string): boolean {
  return pathname === a || pathname.startsWith(a + '/');
}

function Marca() {
  return (
    <span className="inline-flex items-center gap-2">
      <img src="/isotipo.png" alt="" width={20} height={30} />
      <span className="font-titulo text-[17px] font-semibold">Discópico</span>
    </span>
  );
}
